"use client";
import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar, Share2 } from "lucide-react";

interface CalendarModalProps {
  eventName: string;
  description?: string;
  startDate: string;
  endDate: string;
  location: string;
}

const CalendarModal: React.FC<CalendarModalProps> = ({
  eventName,
  description,
  startDate,
  endDate,
  location,
}) => {
  const [isAppleDevice, setIsAppleDevice] = useState(false);

  useEffect(() => {
    // Check if the device is running iOS or macOS
    const checkIfAppleDevice = () => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isMac = /macintosh|mac os x/i.test(userAgent);
      const isIOS =
        /ipad|iphone|ipod/.test(userAgent) && !(window as any).MSStream;
      return isMac || isIOS;
    };

    setIsAppleDevice(checkIfAppleDevice());
  }, []);

  // Standardize event times to 9 PM start on startDate and 4 AM end on next day
  const getStandardizedDates = () => {
    const start = new Date(startDate);
    start.setDate(start.getDate()); // Subtract one day
    start.setHours(21, 0, 0); // 9 PM

    const end = new Date(startDate);
    end.setDate(end.getDate() + 1); // Subtract one day, then add one for next day
    end.setHours(4, 0, 0); // 4 AM

    return { start, end };
  };

  // Format dates for calendar URLs
  const formatDateForCalendar = (date: Date) => {
    return date.toISOString().replace(/-|:|\.\d+/g, "");
  };

  // Generate Google Calendar URL with proper reminders
  const getGoogleCalendarUrl = () => {
    const { start, end } = getStandardizedDates();
    const baseUrl =
      "https://calendar.google.com/calendar/render?action=TEMPLATE";
    const eventDetails = {
      text: eventName,
      details: description || "Underground Hardcore Rave",
      location,
      dates: `${formatDateForCalendar(start)}/${formatDateForCalendar(end)}`,
    };

    // Using proper reminder param for Google Calendar
    return `${baseUrl}&text=${encodeURIComponent(
      eventDetails.text
    )}&details=${encodeURIComponent(
      eventDetails.details
    )}&location=${encodeURIComponent(eventDetails.location)}&dates=${
      eventDetails.dates
    }&recur=RRULE:FREQ=DAILY;COUNT=1`;
  };

  // Generate iCal URL with reminders
  const getICalUrl = () => {
    const { start, end } = getStandardizedDates();
    const icalContent = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `DTSTART:${formatDateForCalendar(start)}`,
      `DTEND:${formatDateForCalendar(end)}`,
      `SUMMARY:${eventName}`,
      `DESCRIPTION:${description || "Underground Hardcore Rave"}`,
      `LOCATION:${location}`,
      // Add reminder 1 week before
      "BEGIN:VALARM",
      "ACTION:DISPLAY",
      "DESCRIPTION:Reminder: One week until the rave!",
      "TRIGGER:-P7D",
      "END:VALARM",
      // Add reminder 1 day before
      "BEGIN:VALARM",
      "ACTION:DISPLAY",
      "DESCRIPTION:Reminder: The rave is tomorrow!",
      "TRIGGER:-P1D",
      "END:VALARM",
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\n");

    const blob = new Blob([icalContent], {
      type: "text/calendar;charset=utf-8",
    });
    return URL.createObjectURL(blob);
  };

  // For non-Apple devices, redirect directly to Google Calendar
  const handleCalendarClick = () => {
    if (!isAppleDevice) {
      window.open(getGoogleCalendarUrl(), "_blank");
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="w-full md:w-auto"
          onClick={handleCalendarClick}
        >
          <Calendar className="w-4 h-4 mr-2" />
          REMIND ME
        </Button>
      </DialogTrigger>
      {isAppleDevice && (
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add to Calendar</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <a
              href={getGoogleCalendarUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="w-full">
                <Share2 className="w-4 h-4 mr-2" />
                Add to Google Calendar
              </Button>
            </a>
            <a href={getICalUrl()} download={`${eventName}.ics`}>
              <Button variant="outline" className="w-full">
                <Calendar className="w-4 h-4 mr-2" />
                Download iCal File
              </Button>
            </a>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default CalendarModal;
